import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { FileText, Lock, Mail, Linkedin, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Reveal, Chapter } from "./Reveal";
import { useLang } from "@/lib/i18n";

// Canonical EN values stored in the database; labels are translated at render time.
const PARTNER_TYPES = ["Investor", "Technology Partner", "Manufacturer", "Market Partner", "Institution", "Other"];
const INTERESTS = ["Investment", "Digital", "Mobility", "Nu Bai", "Energy", "Modular Infrastructure", "Tourism", "Technology Partnership", "Manufacturing", "Other"];
const ORG_TYPES = ["Investor", "Strategic Partner", "Technology Partner", "Financial Institution", "Government / Institution", "Advisor", "Other"];

const inputCls = "w-full bg-ink border border-white/10 px-4 py-3.5 text-sm text-ivory placeholder:text-white/25 focus:border-gold/60 focus:outline-none transition-colors duration-300";

export function Resources() {
  const { t, list, lang } = useLang();
  const dd = (d, f) => (lang === "pt" && d[`${f}_pt`]) ? d[`${f}_pt`] : d[f];
  const [docs, setDocs] = useState([]);
  const [updates, setUpdates] = useState([]);
  const [accessTarget, setAccessTarget] = useState(null); // { resource, title }
  const emptyForm = { name: "", company: "", email: "", country: "", role: "", org_type: "Investor", reason: "", consent: false };
  const [form, setForm] = useState(emptyForm);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get("/documents/all-public-meta").then((r) => setDocs(r.data)).catch(() => {});
    api.get("/updates").then((r) => setUpdates(r.data)).catch(() => {});
  }, []);

  const fileHref = (u) => (u?.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`);

  const statusKey = (d) =>
    !d.available ? "preparation" : d.access === "public" ? (d.title.includes("Overview") ? "publicOverview" : "public") : "controlled";

  const STATUS_LABEL = {
    preparation: t("resources.preparation"),
    public: t("resources.public"),
    publicOverview: `${t("resources.public")} Overview`,
    controlled: t("resources.controlled"),
  };
  const STATUS_CLS = {
    preparation: "text-white/40 border-white/15",
    public: "text-emerald-400 border-emerald-500/40",
    publicOverview: "text-emerald-400 border-emerald-500/40",
    controlled: "text-gold border-gold/40",
  };

  const featured = docs.filter((d) => d.available);
  const upcoming = docs.filter((d) => !d.available);
  const docByMatch = (m) => docs.find((d) => d.title.toLowerCase().includes(m));

  const dataRoomRows = [1, 2, 3, 4, 5, 6, 7].map((n) => ({
    n: `0${n}`,
    name: t(`dataroom.${n}`),
    item: t(`dataroom.${n}.i`),
    doc: n === 1 ? docByMatch("strategic blueprint") : n === 2 ? docByMatch("investment memorandum") : null,
  }));

  const submitAccess = async (e) => {
    e.preventDefault();
    if (!form.consent) {
      toast.error(t("toast.consent.err"));
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/access-requests", { resource: accessTarget.resource, ...form });
      toast.success(`${t("modal.title")} ${data.request_code} ${t("toast.request.ok")}`);
      setAccessTarget(null);
      setForm(emptyForm);
    } catch {
      toast.error(t("toast.request.err"));
    } finally {
      setSending(false);
    }
  };

  const orgLabels = list("modal.orgtypes");

  return (
    <section id="resources" data-testid="resources-section" className="py-28 lg:py-40">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <Chapter index="14" label={t("resources.chapter")} testid="resources-chapter" />
        <Reveal>
          <h2 className="font-serif text-4xl sm:text-5xl tracking-tight text-ivory leading-[1.08]">
            {t("resources.h2a")} <span className="italic text-gold-light">{t("resources.h2b")}</span>
          </h2>
          <p className="mt-6 text-white/55 leading-relaxed max-w-2xl text-base md:text-lg">{t("resources.p")}</p>
        </Reveal>

        {featured.length > 0 && (
          <div className="mt-16 grid lg:grid-cols-2 gap-5">
            {featured.map((d, i) => {
              const conf = d.access === "confidential";
              return (
                <motion.article
                  key={d.id}
                  data-testid={`resource-card-${d.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className={`group relative border overflow-hidden flex flex-col ${conf ? "border-gold/40 bg-[#0B0F18]" : "border-white/10 bg-ink"}`}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.7, delay: i * 0.12 }}
                >
                  {conf && (
                    <>
                      <span className="absolute top-0 left-0 w-10 h-10 border-t-2 border-l-2 border-gold/70 z-10" />
                      <span className="absolute bottom-0 right-0 w-10 h-10 border-b-2 border-r-2 border-gold/70 z-10" />
                    </>
                  )}
                  <div className="relative h-60 lg:h-72 overflow-hidden border-b border-white/10">
                    {d.cover_url ? (
                      <img src={fileHref(d.cover_url)} alt={`${d.title} cover`} className="w-full h-full object-cover object-top opacity-75 transition duration-700 group-hover:opacity-95 group-hover:scale-[1.02]" />
                    ) : (
                      <div className="w-full h-full bg-[#0B1526] flex items-center justify-center">
                        <FileText size={28} className="text-gold/30" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/10 to-transparent" />
                    <span data-testid={`resource-badge-${d.id}`} className={`absolute top-4 left-4 border px-3 py-1 font-mono text-[9px] tracking-[0.25em] uppercase bg-ink/70 backdrop-blur-sm ${conf ? "text-gold-light border-gold/70" : "text-gold border-gold/40"}`}>
                      {conf ? t("resources.confidential") : d.access === "public" ? t("resources.public") : t("resources.controlled")}
                    </span>
                    <span className="absolute bottom-4 right-4 w-9 h-9 border border-white/15 bg-ink/70 backdrop-blur-sm flex items-center justify-center">
                      <Lock size={13} className="text-gold" />
                    </span>
                  </div>
                  <div className="p-8 lg:p-10 flex flex-col flex-1">
                    {dd(d, "category") && <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-gold">{dd(d, "category")}</span>}
                    <h3 className="font-serif text-2xl lg:text-3xl text-ivory mt-3 leading-snug">{dd(d, "title")}</h3>
                    <p className="mt-3 text-sm text-white/55 leading-relaxed flex-1">{dd(d, "description")}</p>
                    <div className="mt-6 flex items-center gap-2 text-white/40">
                      <FileText size={13} />
                      <span className="font-mono text-[10px] tracking-[0.2em] uppercase">{d.pages ? `${d.pages} ${t("resources.pages")} · ` : ""}PDF</span>
                    </div>
                    {d.access === "public" ? (
                      <a data-testid={`resource-view-${d.id}`} href={fileHref(d.file_url)} target="_blank" rel="noopener noreferrer"
                        className="mt-7 inline-flex items-center justify-center gap-2 w-full py-4 bg-gold text-ink font-mono text-[11px] tracking-[0.2em] uppercase hover:bg-gold-light transition-colors duration-300">
                        {d.title.includes("Founder") ? t("resources.viewprofile") : t("resources.viewdoc")} <ArrowUpRight size={13} />
                      </a>
                    ) : (
                      <button data-testid={`resource-request-${d.id}`} onClick={() => setAccessTarget({ resource: d.id, title: d.title })}
                        className={`mt-7 w-full py-4 font-mono text-[11px] tracking-[0.2em] uppercase transition-colors duration-300 inline-flex items-center justify-center gap-2 ${
                          conf ? "bg-gold text-ink hover:bg-gold-light" : "border border-gold/50 text-gold hover:bg-gold/10"
                        }`}>
                        <Lock size={12} /> {t("resources.request")}
                      </button>
                    )}
                  </div>
                </motion.article>
              );
            })}
          </div>
        )}

        {upcoming.length > 0 && (
          <div className={`grid sm:grid-cols-2 gap-4 ${featured.length > 0 ? "mt-5" : "mt-16"}`}>
            {upcoming.map((d, i) => (
              <motion.div
                key={d.id}
                data-testid={`resource-card-${d.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="border border-white/10 bg-ink p-7 flex items-start gap-5 hover:border-gold/30 transition-colors duration-500"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
              >
                <span className="w-10 h-10 shrink-0 border border-white/10 flex items-center justify-center">
                  <FileText size={14} className="text-white/40" />
                </span>
                <div className="flex-1">
                  <div className="flex items-center justify-between gap-3">
                    <h3 className="font-serif text-lg text-ivory">{dd(d, "title")}</h3>
                    <span className={`shrink-0 border px-2 py-0.5 font-mono text-[8px] tracking-[0.2em] uppercase ${STATUS_CLS[statusKey(d)]}`}>
                      {STATUS_LABEL[statusKey(d)]}
                    </span>
                  </div>
                  <p className="mt-1.5 text-sm text-white/45 leading-relaxed">{dd(d, "description")}</p>
                  {d.access === "public" && (
                    <a
                      data-testid={`resource-view-${d.id}`}
                      href={d.title.includes("Founder") ? "#about" : "#bsgotech"}
                      onClick={(e) => { e.preventDefault(); document.querySelector(d.title.includes("Founder") ? "#about" : "#bsgotech")?.scrollIntoView({ behavior: "smooth" }); }}
                      className="mt-4 inline-flex items-center gap-1.5 font-mono text-[10px] tracking-[0.2em] uppercase text-gold hover:text-gold-light transition-colors"
                    >
                      {d.title.includes("Founder") ? t("resources.viewprofile") : t("resources.viewoverview")} <ArrowUpRight size={12} />
                    </a>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        )}

        <Reveal delay={0.15} className="mt-8">
          <div data-testid="data-room-card" className="relative border border-gold/30 bg-surface/60 p-8 lg:p-12 overflow-hidden">
            <div className="absolute -top-24 right-0 w-[420px] h-[420px] bg-gold/[0.06] blur-[110px] rounded-full pointer-events-none" />
            <div className="grid lg:grid-cols-12 gap-10 relative">
              <div className="lg:col-span-6">
                <div className="flex items-center gap-4 mb-6">
                  <span className="w-12 h-12 border border-gold/50 flex items-center justify-center">
                    <Lock size={18} className="text-gold" />
                  </span>
                  <span className="font-mono text-[11px] tracking-[0.3em] uppercase text-gold">{t("resources.confidential")}</span>
                </div>
                <h3 className="font-serif text-3xl sm:text-4xl text-ivory">{t("dataroom.title")}</h3>
                <p className="mt-4 text-white/55 leading-relaxed text-base">{t("dataroom.desc")}</p>
                <button
                  data-testid="data-room-request-btn"
                  onClick={() => setAccessTarget({ resource: "data-room", title: t("dataroom.title") })}
                  className="mt-8 px-8 py-4 bg-gold text-ink font-mono text-[11px] tracking-[0.2em] uppercase hover:bg-gold-light transition-colors duration-300"
                >
                  {t("dataroom.cta")}
                </button>
                <p className="mt-4 font-mono text-[10px] tracking-[0.15em] uppercase text-white/30">{t("dataroom.note")}</p>
              </div>
              <div className="lg:col-span-6 self-center">
                <div className="divide-y divide-white/10 border-y border-white/10">
                  {dataRoomRows.map((r) => (
                    <div key={r.n} data-testid={`data-room-cat-${r.n}`} className="grid grid-cols-[2.5rem_1fr_auto] items-center gap-4 py-3.5">
                      <span className="font-mono text-[11px] tracking-[0.2em] text-gold">{r.n}</span>
                      <div>
                        <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-white/70">{r.name}</span>
                        <p className="text-sm text-white/45 mt-0.5">{r.item}</p>
                      </div>
                      {r.doc?.available ? (
                        <span className="flex items-center gap-1.5 font-mono text-[9px] tracking-[0.15em] uppercase text-gold">
                          <Lock size={10} /> {r.doc.access === "confidential" ? t("resources.confidential") : t("resources.controlled")}
                        </span>
                      ) : (
                        <span className="font-mono text-[9px] tracking-[0.15em] uppercase text-white/30">{t("resources.preparation")}</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.2}>
          <p data-testid="resources-disclosure" className="mt-10 font-mono text-[10px] leading-relaxed tracking-wide text-white/30 max-w-3xl">
            {t("resources.disclosure")}
          </p>
        </Reveal>

        {updates.length > 0 && (
          <div className="mt-16">
            <h3 className="font-mono text-[11px] tracking-[0.3em] uppercase text-white/40 mb-6">{t("updates.title")}</h3>
            <div className="divide-y divide-white/10 border-y border-white/10">
              {updates.map((u) => (
                <div key={u.id} data-testid={`update-${u.id}`} className="py-5 flex flex-col sm:flex-row sm:items-baseline gap-2 sm:gap-8">
                  <span className="font-mono text-[10px] tracking-[0.2em] text-gold shrink-0">{new Date(u.created_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}</span>
                  <div>
                    <p className="text-ivory font-medium">{u.title}</p>
                    {u.body && <p className="text-sm text-white/50 mt-1">{u.body}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {accessTarget && (
        <div className="fixed inset-0 z-[70] bg-ink/85 backdrop-blur-sm flex items-start lg:items-center justify-center p-4 overflow-y-auto" onClick={() => setAccessTarget(null)}>
          <motion.div
            data-testid="access-dialog"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="bg-surface border border-gold/25 p-8 lg:p-10 w-full max-w-2xl my-8 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="absolute top-0 left-0 w-14 h-14 border-t border-l border-gold/60" />
            <div className="absolute bottom-0 right-0 w-14 h-14 border-b border-r border-gold/60" />
            <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-gold">{t("resources.controlled")}</span>
            <h3 className="font-serif text-3xl text-ivory mt-3">{t("modal.title")}</h3>
            <p className="mt-1 text-sm text-white/50">{t("modal.sub")}</p>
            <form onSubmit={submitAccess} className="mt-8 grid sm:grid-cols-2 gap-4">
              <input data-testid="access-name" required placeholder={t("modal.name")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
              <input data-testid="access-company" placeholder={t("modal.company")} value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className={inputCls} />
              <input data-testid="access-email" required type="email" placeholder={t("modal.email")} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} />
              <input data-testid="access-country" placeholder={t("modal.country")} value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className={inputCls} />
              <input data-testid="access-role" placeholder={t("modal.role")} value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className={inputCls} />
              <select data-testid="access-org-type" value={form.org_type} onChange={(e) => setForm({ ...form, org_type: e.target.value })} className={`${inputCls} appearance-none`}>
                {ORG_TYPES.map((v, i) => <option key={v} value={v} className="bg-ink">{orgLabels[i] || v}</option>)}
              </select>
              <div className="sm:col-span-2">
                <label className="block font-mono text-[10px] tracking-[0.25em] uppercase text-white/40 mb-2">{t("modal.resource")}</label>
                <div data-testid="access-resource" className="w-full border border-gold/30 bg-gold/[0.05] px-4 py-3.5 text-sm text-gold font-mono tracking-wide">{accessTarget.title}</div>
              </div>
              <textarea data-testid="access-reason" rows={4} placeholder={t("modal.reason")} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className={`${inputCls} sm:col-span-2`} />
              <label className="sm:col-span-2 flex items-start gap-3 text-xs text-white/50 leading-relaxed cursor-pointer">
                <input data-testid="access-consent" type="checkbox" checked={form.consent} onChange={(e) => setForm({ ...form, consent: e.target.checked })} className="mt-0.5 accent-[#D4AF37]" />
                {t("modal.consent")}
              </label>
              <button data-testid="access-submit-btn" disabled={sending} className="sm:col-span-2 py-4 bg-gold text-ink font-mono text-[11px] tracking-[0.2em] uppercase hover:bg-gold-light transition-colors disabled:opacity-50">
                {sending ? t("modal.sending") : t("modal.submit")}
              </button>
            </form>
          </motion.div>
        </div>
      )}
    </section>
  );
}

export function Connect() {
  const { t, list } = useLang();
  const partnerLabels = list("modal.orgtypes"); // reused ordering not aligned; use dedicated labels below
  const [type, setType] = useState("Investor");
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/inquiries", { partner_type: type, ...form });
      toast.success(t("toast.connect.ok"));
      setForm({ name: "", email: "", company: "", message: "" });
    } catch {
      toast.error(t("toast.connect.err"));
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="connect" data-testid="connect-section" className="py-28 lg:py-40 bg-surface/40 border-y border-white/10 relative overflow-hidden">
      <div className="absolute -top-20 left-1/3 w-[500px] h-[500px] bg-gold/[0.05] blur-[130px] rounded-full pointer-events-none" />
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-16 relative">
        <div className="lg:col-span-6">
          <Chapter index="15" label={t("connect.chapter")} testid="connect-chapter" />
          <Reveal>
            <h2 className="font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tight text-ivory leading-[1.02]">
              {t("connect.h2a")} <span className="italic text-gold-light">{t("connect.h2b")}</span>
            </h2>
            <p className="mt-8 text-white/60 leading-relaxed text-base md:text-lg max-w-lg">{t("connect.p")}</p>
          </Reveal>
          <Reveal delay={0.15} className="mt-10 flex flex-wrap gap-3">
            {PARTNER_TYPES.map((v) => (
              <button
                key={v}
                data-testid={`connect-type-${v.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => setType(v)}
                className={`px-5 py-2.5 font-mono text-[10px] tracking-[0.2em] uppercase transition-colors duration-300 border ${
                  type === v ? "border-gold bg-gold/10 text-gold" : "border-white/15 text-white/55 hover:border-gold/50 hover:text-gold"
                }`}
              >
                {v}
              </button>
            ))}
          </Reveal>
        </div>

        <Reveal delay={0.2} className="lg:col-span-5 lg:col-start-8">
          <form data-testid="connect-form" onSubmit={submit} className="space-y-4 border border-white/10 bg-ink/70 p-8">
            <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-white/40">{t("connect.as")} · <span className="text-gold">{type}</span></p>
            <input data-testid="connect-name" required placeholder={t("modal.name")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
            <input data-testid="connect-email" required type="email" placeholder={t("modal.email")} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} />
            <input data-testid="connect-company" placeholder={t("modal.company")} value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className={inputCls} />
            <textarea data-testid="connect-message" rows={4} placeholder={t("connect.message")} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className={inputCls} />
            <button data-testid="connect-submit-btn" disabled={sending} className="w-full py-4 bg-gold text-ink font-mono text-[11px] tracking-[0.2em] uppercase hover:bg-gold-light transition-colors disabled:opacity-50">
              {sending ? t("connect.sending") : t("connect.cta")}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}

export function Contact({ settings }) {
  const { t, list } = useLang();
  const [form, setForm] = useState({ name: "", company: "", email: "", country: "", interest: "Investment", message: "" });
  const [sending, setSending] = useState(false);
  const interestLabels = list("contact.interests");

  useEffect(() => {
    const handler = (e) => {
      setForm((f) => ({ ...f, interest: e.detail }));
      document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" });
    };
    window.addEventListener("bsg:contact-interest", handler);
    return () => window.removeEventListener("bsg:contact-interest", handler);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success(t("toast.contact.ok"));
      setForm({ name: "", company: "", email: "", country: "", interest: "Investment", message: "" });
    } catch {
      toast.error(t("toast.contact.err"));
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-28 lg:py-40">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-16">
        <div className="lg:col-span-5">
          <Chapter index="16" label={t("contact.chapter")} testid="contact-chapter" />
          <Reveal>
            <h2 className="font-serif text-4xl sm:text-5xl tracking-tight text-ivory leading-[1.08]">
              {t("contact.h2a")} <span className="italic text-gold-light">{t("contact.h2b")}</span>
            </h2>
          </Reveal>
          <Reveal delay={0.15} className="mt-12 space-y-5">
            <a data-testid="contact-email-link" href={`mailto:${settings?.founder_email || "contact@bsgotech.com"}`} className="flex items-center gap-4 text-white/60 hover:text-gold transition-colors group">
              <span className="w-11 h-11 border border-white/15 flex items-center justify-center group-hover:border-gold/60 transition-colors"><Mail size={16} /></span>
              <span className="font-mono text-xs tracking-[0.15em]">{settings?.founder_email || "contact@bsgotech.com"}</span>
            </a>
            {settings?.linkedin && (
              <a data-testid="contact-linkedin-link" href={settings.linkedin} target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 text-white/60 hover:text-gold transition-colors group">
                <span className="w-11 h-11 border border-white/15 flex items-center justify-center group-hover:border-gold/60 transition-colors"><Linkedin size={16} /></span>
                <span className="font-mono text-xs tracking-[0.15em]">LinkedIn</span>
              </a>
            )}
            <p className="pt-6 font-mono text-[10px] tracking-[0.25em] uppercase text-white/30">{t("hero.location")}</p>
          </Reveal>
        </div>

        <Reveal delay={0.2} className="lg:col-span-7">
          <form data-testid="contact-form" onSubmit={submit} className="grid sm:grid-cols-2 gap-4">
            <input data-testid="contact-name" required placeholder={t("contact.name")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
            <input data-testid="contact-company" placeholder={t("contact.company")} value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className={inputCls} />
            <input data-testid="contact-email" required type="email" placeholder={t("contact.email")} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} />
            <input data-testid="contact-country" placeholder={t("contact.country")} value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className={inputCls} />
            <select data-testid="contact-interest" value={form.interest} onChange={(e) => setForm({ ...form, interest: e.target.value })} className={`${inputCls} sm:col-span-2 appearance-none`}>
              {INTERESTS.map((v, i) => <option key={v} value={v} className="bg-ink">{interestLabels[i] || v}</option>)}
            </select>
            <textarea data-testid="contact-message" required rows={5} placeholder={t("contact.message")} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className={`${inputCls} sm:col-span-2`} />
            <button data-testid="contact-submit-btn" disabled={sending} className="sm:col-span-2 py-4 bg-gold text-ink font-mono text-[11px] tracking-[0.2em] uppercase hover:bg-gold-light transition-colors disabled:opacity-50">
              {sending ? t("contact.sending") : t("contact.cta")}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}

export function Footer({ settings }) {
  const { t } = useLang();
  const go = (href) => document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  const links = [
    { label: t("nav.about"), href: "#about" }, { label: t("nav.bsgotech"), href: "#bsgotech" }, { label: t("nav.projects"), href: "#projects" },
    { label: "HX", href: "#division-02" }, { label: t("nav.resources"), href: "#resources" }, { label: t("nav.contact"), href: "#contact" },
  ];
  return (
    <footer data-testid="site-footer" className="border-t border-white/10 bg-surface/40">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16 grid md:grid-cols-3 gap-12">
        <div>
          <p className="font-serif text-2xl text-ivory">Cleiton Barreto dos Santos</p>
          <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-gold mt-2">{t("footer.role")}</p>
          <p className="mt-6 font-serif text-xl text-white/70">BSGOTECH</p>
          <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-white/35 mt-1">{t("hero.location")}</p>
        </div>
        <div className="flex flex-col gap-3">
          {links.map((l) => (
            <button key={l.href} data-testid={`footer-link-${l.href.slice(1)}`} onClick={() => go(l.href)} className="text-left font-mono text-[11px] tracking-[0.2em] uppercase text-white/50 hover:text-gold transition-colors w-max">
              {l.label}
            </button>
          ))}
          {settings?.linkedin && (
            <a data-testid="footer-linkedin" href={settings.linkedin} target="_blank" rel="noopener noreferrer" className="font-mono text-[11px] tracking-[0.2em] uppercase text-white/50 hover:text-gold transition-colors w-max">
              LinkedIn
            </a>
          )}
        </div>
        <div>
          <p data-testid="footer-legal" className="text-xs text-white/35 leading-relaxed">{t("footer.legal")}</p>
          <p className="mt-6 font-mono text-[10px] tracking-[0.2em] uppercase text-white/25">© {new Date().getFullYear()} Cleiton Barreto dos Santos · BSGOTECH</p>
          <a data-testid="footer-admin-link" href="/admin" className="mt-3 inline-block font-mono text-[10px] tracking-[0.2em] uppercase text-white/25 hover:text-gold transition-colors">
            {t("footer.admin")}
          </a>
        </div>
      </div>
    </footer>
  );
}
